import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout';
import { SelectionCard } from '../../components/SelectionCard';
import { MultiSelectChips } from '../../components/ui/MultiSelectChips';
import { BrandProfile } from '../../types/profile';
const STEPS = [{
  id: 'identity',
  title: 'Brand Identity',
  description: 'Tell us about your brand'
}, {
  id: 'goals',
  title: 'Sponsorship Goals',
  description: 'What are you looking to achieve?'
}, {
  id: 'audience',
  title: 'Target Audience',
  description: 'Who do you want to reach?'
}, {
  id: 'offering',
  title: 'Sponsorship Offering',
  description: 'What can you provide?'
}, {
  id: 'preferences',
  title: 'Preferences',
  description: 'Event types you prefer'
}, {
  id: 'summary',
  title: 'Review',
  description: 'Confirm your profile'
}];
const INDUSTRIES = ['Food & Beverage', 'Tech', 'Fashion', 'Beauty', 'Health & Wellness', 'Sports', 'Entertainment', 'Finance', 'Automotive', 'Other'];
const GOALS = ['Brand Awareness', 'Lead Generation', 'Product Sampling', 'Content Creation', 'Sales', 'Community Engagement'];
const REGIONS = ['North America', 'Europe', 'Asia', 'South America', 'Global', 'Remote/Online'];
const AGE_GROUPS = ['Gen Z (18-24)', 'Millennials (25-40)', 'Gen X (41-56)', 'Boomers (57+)'];
const AUDIENCE_TYPES = ['Students', 'Professionals', 'Parents', 'Tech Enthusiasts', 'Creatives', 'Gamers', 'Fitness Buffs', 'Luxury Shoppers'];
const SPONSORSHIP_TYPES = ['Financial', 'In-Kind Product', 'Merchandise', 'Services', 'Media/Promotion'];
const BUDGET_RANGES = ['Under $1k', '$1k - $5k', '$5k - $10k', '$10k - $50k', '$50k+'];
const EVENT_CATEGORIES = ['Conferences', 'Festivals', 'Sports', 'Community', 'Arts', 'Esports', 'Workshops', 'Networking'];
export function BrandOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<BrandProfile>>({
    companyName: '',
    industry: '',
    description: '',
    marketingGoals: '',
    activationRegions: [],
    targetAgeGroups: [],
    targetAudienceTypes: [],
    sponsorshipType: [],
    productsForSampling: [],
    budgetRange: '',
    preferredEventCategories: [],
    avoidCategories: []
  });
  const updateData = (key: keyof BrandProfile, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Submit logic here
      console.log('Submitting Brand Profile:', formData);
      navigate('/dashboard/brand');
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Identity
        return <div className="space-y-6" data-id="element-2926">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2927">Brand Identity</h3>
            <div data-id="element-2928">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2929">
                Brand Name
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g. Acme Corp" value={formData.companyName} onChange={e => updateData('companyName', e.target.value)} data-id="element-2930" />
            </div>
            <div data-id="element-2931">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2932">
                Industry
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-2933">
                {INDUSTRIES.map(ind => <SelectionCard key={ind} id={ind} label={ind} isSelected={formData.industry === ind} onClick={() => updateData('industry', ind)} className="p-3" data-id="element-2934" />)}
              </div>
            </div>
            <div data-id="element-2935">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2936">
                Description
              </label>
              <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" rows={3} placeholder="Briefly describe your brand and mission..." value={formData.description} onChange={e => updateData('description', e.target.value)} data-id="element-2937" />
            </div>
          </div>;
      case 2:
        // Goals
        return <div className="space-y-8" data-id="element-2938">
            <div data-id="element-2939">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2940">
                Sponsorship Goals
              </h3>
              <MultiSelectChips options={GOALS} selected={formData.marketingGoals ? formData.marketingGoals.split(',').filter(Boolean) : []} onChange={selected => updateData('marketingGoals', selected.join(','))} data-id="element-2941" />
            </div>
            <div data-id="element-2942">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2943">
                Activation Regions
              </h3>
              <MultiSelectChips options={REGIONS} selected={formData.activationRegions || []} onChange={selected => updateData('activationRegions', selected)} data-id="element-2944" />
            </div>
          </div>;
      case 3:
        // Audience
        return <div className="space-y-8" data-id="element-2945">
            <div data-id="element-2946">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2947">
                Target Age Groups
              </h3>
              <MultiSelectChips options={AGE_GROUPS} selected={formData.targetAgeGroups || []} onChange={selected => updateData('targetAgeGroups', selected)} data-id="element-2948" />
            </div>
            <div data-id="element-2949">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2950">
                Audience Types
              </h3>
              <MultiSelectChips options={AUDIENCE_TYPES} selected={formData.targetAudienceTypes || []} onChange={selected => updateData('targetAudienceTypes', selected)} data-id="element-2951" />
            </div>
          </div>;
      case 4:
        // Offering
        return <div className="space-y-6" data-id="element-2952">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2953">
              Sponsorship Offering
            </h3>

            <div data-id="element-2954">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2955">
                Sponsorship Type
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-2956">
                {SPONSORSHIP_TYPES.map(type => <SelectionCard key={type} id={type} label={type} isSelected={formData.sponsorshipType?.includes(type) || false} onClick={() => {
                const current = formData.sponsorshipType || [];
                const updated = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
                updateData('sponsorshipType', updated);
              }} className="p-3" data-id="element-2957" />)}
              </div>
            </div>

            {formData.sponsorshipType?.includes('In-Kind Product') && <div data-id="element-2958">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2959">
                  Products for Sampling
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Energy Drinks, Protein Bars (comma separated)" value={formData.productsForSampling?.join(', ')} onChange={e => updateData('productsForSampling', e.target.value.split(',').map(s => s.trim()))} data-id="element-2960" />
              </div>}

            <div data-id="element-2961">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2962">
                Budget Range
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-2963">
                {BUDGET_RANGES.map(range => <button key={range} onClick={() => updateData('budgetRange', range)} className={`px-3 py-2 rounded-lg text-sm border ${formData.budgetRange === range ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-2964">
                    {range}
                  </button>)}
              </div>
            </div>
          </div>;
      case 5:
        // Preferences
        return <div className="space-y-8" data-id="element-2965">
            <div data-id="element-2966">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2967">
                Preferred Event Categories
              </h3>
              <MultiSelectChips options={EVENT_CATEGORIES} selected={formData.preferredEventCategories || []} onChange={selected => updateData('preferredEventCategories', selected)} data-id="element-2968" />
            </div>
            <div data-id="element-2969">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2970">
                Categories to Avoid (Optional)
              </h3>
              <MultiSelectChips options={EVENT_CATEGORIES} selected={formData.avoidCategories || []} onChange={selected => updateData('avoidCategories', selected)} data-id="element-2971" />
            </div>
          </div>;
      case 6:
        // Summary
        return <div className="space-y-6" data-id="element-2972">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2973">Review Profile</h3>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4" data-id="element-2974">
              <SummarySection title="Identity" onEdit={() => setCurrentStep(1)} data-id="element-2975">
                <p className="font-medium" data-id="element-2976">{formData.companyName}</p>
                <p className="text-sm text-gray-500" data-id="element-2977">{formData.industry}</p>
              </SummarySection>

              <SummarySection title="Goals" onEdit={() => setCurrentStep(2)} data-id="element-2978">
                <div className="flex flex-wrap gap-1" data-id="element-2979">
                  {formData.marketingGoals?.split(',').map(g => <span key={g} className="text-xs bg-white border px-2 py-1 rounded" data-id="element-2980">
                      {g}
                    </span>)}
                </div>
              </SummarySection>

              <SummarySection title="Audience" onEdit={() => setCurrentStep(3)} data-id="element-2981">
                <p className="text-sm text-gray-600" data-id="element-2982">
                  Targeting {formData.targetAgeGroups?.length} age groups and{' '}
                  {formData.targetAudienceTypes?.length} audience types.
                </p>
              </SummarySection>

              <SummarySection title="Offering" onEdit={() => setCurrentStep(4)} data-id="element-2983">
                <p className="text-sm text-gray-600" data-id="element-2984">
                  Budget: {formData.budgetRange}
                </p>
                <div className="flex flex-wrap gap-1 mt-1" data-id="element-2985">
                  {formData.sponsorshipType?.map(t => <span key={t} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded" data-id="element-2986">
                      {t}
                    </span>)}
                </div>
              </SummarySection>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <OnboardingLayout currentStep={currentStep} totalSteps={STEPS.length} steps={STEPS} title="Brand Setup" description="Create your brand profile to match with events." onBack={handleBack} onNext={handleNext} nextLabel={currentStep === STEPS.length ? 'Create Profile' : 'Next'} data-id="element-2987">
      {renderStepContent()}
    </OnboardingLayout>;
}
function SummarySection({
  title,
  children,
  onEdit
}: {
  title: string;
  children: React.ReactNode;
  onEdit: () => void;
}) {
  return <div className="border-b border-gray-200 pb-4 last:border-0 last:pb-0" data-id="element-2988">
      <div className="flex justify-between items-center mb-2" data-id="element-2989">
        <h4 className="text-sm font-semibold text-gray-900" data-id="element-2990">{title}</h4>
        <button onClick={onEdit} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-2991">
          Edit
        </button>
      </div>
      {children}
    </div>;
}